import { BsdsCheckbox } from '@bsds-lib-component/react';
import React, { useCallback, useRef } from 'react';

import type { InvoiceHistoryItem, InvoiceHistoryTableProps } from '../../../types/vida/table/invoiceHistoryTableType';
import { generateInvoicesXLSX } from '../../../utils/invoiceExcelUtils';
import { generateInvoicePDF } from '../../../utils/invoicePdfUtils';
import { formatSubestipulanteCodNome } from '../../../utils/subestipulanteUtil';
import Pagination from '../../pagination/Pagination';
import Tooltip from '../../tooltip/Tooltip';
import TableHeaderBulkActionsMenu from '../shared/TableHeaderBulkActionsMenu';
import TableItemActionsMenu from '../shared/TableItemActionsMenu';
import TableMenuItemButton from '../shared/TableMenuItemButton';
import { createMenuItemActionHandlers, registerMapRef } from '../shared/tableMenuUtils';
import { useTableHeaderMenu } from '../shared/useTableHeaderMenu';
import { useTableItemMenu } from '../shared/useTableItemMenu';
import { useTableRowSelection } from '../shared/useTableRowSelection';

const TABLE_COLUMN_COUNT = 9;

const truncateCellClass = 'px-4 py-2 max-w-0 truncate';

const TruncateCell: React.FC<{ value: string; tooltip?: string }> = ({ value, tooltip }) => (
  <Tooltip label={tooltip ?? value}>
    <span className="block truncate">{value}</span>
  </Tooltip>
);

const InvoiceHistoryTable: React.FC<InvoiceHistoryTableProps> = ({
  invoices,
  formatCurrency,
  formatDate,
  formatBillingMonth,
  getStatusBadgeClass,
  onRowClick,
  emptyMessage = 'Nenhuma fatura encontrada',
  onSelectionChange,
  onExport,
  onExportPdf,
  currentPage,
  totalPages,
  totalItems,
  onPageChange,
}) => {
  const itemConsultButtonRefs = useRef<Map<string | number, HTMLButtonElement>>(new Map());
  const itemPdfButtonRefs = useRef<Map<string | number, HTMLButtonElement>>(new Map());

  const getFocusTargetOnOpen = useCallback((openId: string | number) => {
    if (onRowClick) {
      return itemConsultButtonRefs.current.get(openId);
    }
    return itemPdfButtonRefs.current.get(openId);
  }, [onRowClick]);

  const {
    closeItemMenu,
    isItemMenuOpen,
    handleItemMenuToggle,
    handleItemMenuKeyDown,
    focusMenuButton,
    registerItemMenuRef,
    registerItemMenuButtonRef,
  } = useTableItemMenu<string | number>({ getFocusTargetOnOpen });

  const {
    isMenuOpen,
    closeMenu,
    toggleMenu,
    menuRef,
    menuButtonRef,
    actionButtonRef,
    menuButtonId,
    handleMenuKeyDown,
    createActionKeyDownHandler,
  } = useTableHeaderMenu();

  const {
    allSelected,
    hasSelectedItems,
    handleSelectAll,
    handleSelectItem,
    isSelected,
    getSelectedItems,
  } = useTableRowSelection({
    itemIds: invoices.map((invoice) => invoice.id),
    onSelectionChange,
  });

  const handleExport = async () => {
    if (!hasSelectedItems) {
      return;
    }

    const selectedInvoices = getSelectedItems(invoices);

    if (onExport) {
      onExport(selectedInvoices);
    } else {
      await generateInvoicesXLSX(selectedInvoices, formatCurrency, formatDate, formatBillingMonth);
    }

    closeMenu();
  };

  const consultActionHandlers = createMenuItemActionHandlers<InvoiceHistoryItem>({
    onAction: (invoice) => onRowClick?.(invoice),
    closeMenu: closeItemMenu,
    focusMenuButton: (invoice) => focusMenuButton(invoice.id),
  });

  const exportPdfActionHandlers = createMenuItemActionHandlers<InvoiceHistoryItem>({
    onAction: (invoice) => {
      if (onExportPdf) {
        void onExportPdf(invoice);
      } else {
        void generateInvoicePDF(invoice, formatCurrency, formatDate, formatBillingMonth);
      }
    },
    closeMenu: closeItemMenu,
    focusMenuButton: (invoice) => focusMenuButton(invoice.id),
  });

  const registerConsultRef = (invoiceId: string | number, element: HTMLButtonElement | null) => {
    registerMapRef(itemConsultButtonRefs.current, invoiceId, element);
  };

  const registerPdfRef = (invoiceId: string | number, element: HTMLButtonElement | null) => {
    registerMapRef(itemPdfButtonRefs.current, invoiceId, element);
  };

  return (
    <>
      <div className="w-full max-w-full min-h-[150px] overflow-x-auto custom-scrollbar rounded-md shadow-xs">
        <table
          className="min-w-[1280px] w-full table-fixed"
          aria-label="Histórico de faturas"
        >
          <thead>
            <tr>
              <th
                className="px-4 py-2 text-left text-sm font-semibold border-b border-gray-100 whitespace-nowrap w-16"
                scope="col"
                aria-label="Ações da tabela"
              >
                <TableHeaderBulkActionsMenu
                  isOpen={isMenuOpen}
                  menuButtonId={menuButtonId}
                  menuRef={menuRef}
                  menuButtonRef={menuButtonRef}
                  actionButtonRef={actionButtonRef}
                  actionLabel="Exportar em Excel"
                  actionDisabled={!hasSelectedItems}
                  onToggle={(event) => {
                    event.stopPropagation();
                    toggleMenu();
                  }}
                  onMenuKeyDown={handleMenuKeyDown}
                  onAction={(event) => {
                    event.stopPropagation();
                    void handleExport();
                  }}
                  onActionKeyDown={createActionKeyDownHandler(() => void handleExport())}
                />
              </th>
              <th className="px-2 py-2 text-left text-sm font-semibold border-b border-gray-100 whitespace-nowrap w-16" scope="col">
                <Tooltip label="Selecionar todas as faturas">
                  <BsdsCheckbox
                    checked={allSelected}
                    onBsdsChange={handleSelectAll}
                    size="small"
                    color="secondary"
                    aria-label="Selecionar todas as faturas"
                  />
                </Tooltip>
              </th>
              <th className="px-4 py-2 text-left text-sm font-semibold border-b border-gray-100 whitespace-nowrap w-36" scope="col">
                <Tooltip label="Mês da Fatura">
                  <span>Mês da Fatura</span>
                </Tooltip>
              </th>
              <th className="px-4 py-2 text-left text-sm font-semibold border-b border-gray-100 w-44" scope="col">
                <Tooltip label="Número do Contrato">
                  <span className="block truncate">Número do Contrato</span>
                </Tooltip>
              </th>
              <th className="px-4 py-2 pl-6 text-left text-sm font-semibold border-b border-gray-100 w-60" scope="col">
                <Tooltip label="Nome do sub-Estipulante">
                  <span className="block truncate">Sub-Estipulante</span>
                </Tooltip>
              </th>
              <th className="px-4 py-2 text-left text-sm font-semibold border-b border-gray-100 whitespace-nowrap w-24" scope="col">
                <Tooltip label="Tipo da fatura">
                  <span>Tipo</span>
                </Tooltip>
              </th>
              <th className="px-4 py-2 text-left text-sm font-semibold border-b border-gray-100 whitespace-nowrap w-28" scope="col">
                <Tooltip label="Data de vencimento">
                  <span>Vencimento</span>
                </Tooltip>
              </th>
              <th className="px-4 py-2 text-left text-sm font-semibold border-b border-gray-100 whitespace-nowrap w-28" scope="col">
                <Tooltip label="Valor da fatura">
                  <span>Valor</span>
                </Tooltip>
              </th>
              <th className="px-4 py-2 text-left text-sm font-semibold border-b border-gray-100 whitespace-nowrap w-32" scope="col">
                <Tooltip label="Status da fatura">
                  <span>Status</span>
                </Tooltip>
              </th>
            </tr>
          </thead>

          <tbody className="divide-y divide-gray-50 text-text-color">
            {invoices && invoices.length > 0 ? (
              invoices.map((invoice: InvoiceHistoryItem) => {
                const month = invoice.billingMonth
                  ? (formatBillingMonth ? formatBillingMonth(invoice.billingMonth) : invoice.billingMonth)
                  : (invoice.invoiceMonth || '');
                const policyNumber = invoice.contractNumber || invoice.policyNumber || '';
                const subestipulanteLabel = formatSubestipulanteCodNome(
                  invoice.codSubestipulante,
                  invoice.nomeSubestipulante,
                );

                return (
                  <tr
                    key={invoice.id}
                    className="hover:bg-gray-50 transition-colors text-xs"
                    aria-selected={isSelected(invoice.id)}
                  >
                    <td className="px-4 py-2 whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                      <TableItemActionsMenu
                        itemId={invoice.id}
                        isOpen={isItemMenuOpen(invoice.id)}
                        menuId={`invoice-menu-${invoice.id}`}
                        triggerLabel={`Ações da fatura ${month}`}
                        menuLabel={`Menu de ações da fatura ${month}`}
                        triggerTooltip={`Ações da fatura ${month}`}
                        onToggle={(event) => handleItemMenuToggle(invoice.id, event)}
                        onKeyDown={(event) => handleItemMenuKeyDown(invoice.id, event)}
                        registerMenuRef={(element) => registerItemMenuRef(invoice.id, element)}
                        registerButtonRef={(element) => registerItemMenuButtonRef(invoice.id, element)}
                      >
                        {onRowClick && (
                          <TableMenuItemButton
                            registerRef={(element) => registerConsultRef(invoice.id, element)}
                            onClick={(event) => consultActionHandlers.handleClick(invoice, event)}
                            onKeyDown={(event) => consultActionHandlers.handleKeyDown(invoice, event)}
                          >
                            Consultar
                          </TableMenuItemButton>
                        )}
                        <TableMenuItemButton
                          registerRef={(element) => registerPdfRef(invoice.id, element)}
                          onClick={(event) => exportPdfActionHandlers.handleClick(invoice, event)}
                          onKeyDown={(event) => exportPdfActionHandlers.handleKeyDown(invoice, event)}
                        >
                          Exportar em PDF
                        </TableMenuItemButton>
                      </TableItemActionsMenu>
                    </td>
                    <td className="px-2 py-2 whitespace-nowrap text-left" onClick={(e) => e.stopPropagation()}>
                      <Tooltip label="Selecionar fatura">
                        <BsdsCheckbox
                          checked={isSelected(invoice.id)}
                          onBsdsChange={() => handleSelectItem(invoice.id)}
                          size="small"
                          color="secondary"
                          aria-label={`Selecionar fatura de ${month}, apólice ${policyNumber}`}
                        />
                      </Tooltip>
                    </td>
                    <td className={truncateCellClass}>
                      <TruncateCell value={month} />
                    </td>
                    <td className={`${truncateCellClass} w-44`}>
                      <TruncateCell value={policyNumber} />
                    </td>
                    <td className={`${truncateCellClass} w-60 pl-6`}>
                      <TruncateCell value={subestipulanteLabel} />
                    </td>
                    <td className={truncateCellClass}>
                      <TruncateCell value={invoice.type} />
                    </td>
                    <td className="px-4 py-2 whitespace-nowrap">{formatDate(invoice.dueDate)}</td>
                    <td className="px-4 py-2 whitespace-nowrap">{formatCurrency(invoice.amount)}</td>
                    <td className="px-4 py-2 whitespace-nowrap">
                      <span className={`text-xs py-0.5 rounded-xs inline-block min-w-[90px] text-center ${getStatusBadgeClass(invoice.status, 'table')}`}>
                        {invoice.status}
                      </span>
                    </td>
                  </tr>
                );
              })
            ) : (
              <tr>
                <td
                  tabIndex={-1}
                  colSpan={TABLE_COLUMN_COUNT}
                  className="px-4 py-4 text-left sm:text-center text-text-color text-xs focus-visible:outline  focus-visible:outline-offset-2 focus-visible:outline-gray-500"
                  role="status"
                  aria-live="polite"
                >
                  {emptyMessage}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {onPageChange != null
        && currentPage != null
        && totalPages != null
        && totalItems != null
        && totalItems > 0 && (
          <div className="bsds-mt-16 bsds-mb-16">
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={onPageChange}
              totalItems={totalItems}
              itemLabel="Faturas"
              alwaysShow
            />
          </div>
        )}
    </>
  );
};

export default InvoiceHistoryTable;
